import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config.js";
import DownloadButtons from "../DownloadButtons/DownloadButtons.js";
import EntriesSelector from "../Pagination/EntriesSelector";
import Pagination from "../Pagination/Paginations";
import { reportTableStyles } from "./reportTableStyles";

const LeaveAvailable = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // 🔹 Fetch API
  const fetchLeaveAvailableReport = async () => {
    // block repeat clicks while a request is already running
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await axios.get(API_BASE_URL + "/leave_available_report", {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });

      setRecords(res.data.records || []);
      toast.success("Leave availability loaded successfully!", {
        position: "bottom-right",
        theme: "colored",
        toastId: "leave-available-success",
      });
    } catch (err) {
      toast.error("Failed to load leave availability report", { toastId: "leave-available-error" });
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveAvailableReport();
  }, []);

  // 🔹 Search filter
  const filteredRecordsAll = records.filter((item) =>
    `${item.employee_id} ${item.employee_name} ${item.department}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  /* Pagination (Previous / Next style) */
  const totalPages = Math.ceil(filteredRecordsAll.length / perPage);
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const filteredRecords = filteredRecordsAll.slice(indexOfFirst, indexOfLast);

  // 🔹 DataTable Columns
  const columns = [
    {
      name: "Employee ID",
      selector: row => row.employee_id,
      sortable: true,
    },
    {
      name: "Employee Name",
      selector: row => row.employee_name,
      sortable: true,
    },
    {
      name: "Department",
      selector: row => row.department,
      sortable: true,
    },
    {
      name: "Available CL",
      selector: row => row.available_cl,
      sortable: true,
      right: true,
    },
    {
      name: "Available SL",
      selector: row => row.available_sl,
      sortable: true,
      right: true,
    },
    {
      name: "Available ML",
      selector: row => row.available_ml,
      sortable: true,
      right: true,
    },
    {
      name: "Available PL",
      selector: row => row.available_pl,
      sortable: true,
      right: true,
    },
  ];

  // 🔹 Custom styles (optional)

  return (
    <div className="container-fluid mt-4">
      <ToastContainer />

      <div className="card">
        <h6 className="card-header d-flex align-items-center" style={{ color: "#1d3a8a", fontWeight: 600 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="#1d3a8a" style={{ marginRight: "8px", verticalAlign: "-3px" }}>
            <path fillRule="evenodd" d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m1.679-4.493-1.335 2.226a.75.75 0 0 1-1.174.144l-.774-.773a.5.5 0 0 1 .708-.708l.547.548 1.017-1.694a.5.5 0 1 1 .858.257z"/>
            <path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0M8 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4"/>
            <path d="M8.256 14a4.5 4.5 0 0 1-.229-1.004H3c.001-.246.154-.986.832-1.664C4.484 10.68 5.711 10 8 10q.39 0 .74.025c.226-.341.496-.65.804-.918Q8.844 9.002 8 9c-5 0-6 3-6 4s1 1 1 1z"/>
          </svg>
          Employee Leave Availability
        </h6>

        <div className="card-body">
          {/* 🔸 Top controls */}
          <div className="d-flex justify-content-between mb-3 align-items-center">
            <DownloadButtons
              data={filteredRecordsAll}
              filename="Leave_Available_Report"
            />

            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <EntriesSelector
                entriesPerPage={perPage}
                setEntriesPerPage={setPerPage}
                setCurrentPage={setCurrentPage}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                style={{ width: "220px" }}
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>
          </div>

          {/* 🔸 DataTable */}
          <DataTable
            columns={columns}
            data={filteredRecords}
            fixedHeader
            fixedHeaderScrollHeight="60vh"
            progressPending={loading}
            highlightOnHover
            striped
            dense
            customStyles={reportTableStyles}
            noDataComponent={
              <div className="p-3 text-danger fw-bold">
                No records found
              </div>
            }
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
};

export default LeaveAvailable;
